import { useContext, useMemo } from 'react';
import { transformFilterIntoNextRequest } from 'components/filter/filters-next/upstream';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { TableCoreResourceContext } from 'components/table/TableCoreResourceContext';
import { ITableCoreResourceContext, UseTableStateOptions } from 'components/table/types';
import { useReactTableState } from 'components/table/useReactTableState';
import { getTableResourceFromReportResource } from 'components/table/util';
import useDebounce from 'customHooks/useDebounce';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { useTableSchema } from 'customHooks/useTableSchema';
import { Column, ReportResource } from 'types/domain';

const useCoreTableState = <N extends ReportResource>(
    resource: N,
    options: UseTableStateOptions = {},
) => {
    const { params: paramsConsumer = {}, initialTableState = {}, debounce = {} } = options;
    const {
        search: searchDebounce = 250,
        sorting: sortingDebounce = 0,
        columnVisibility: columnVisibilityDebounce = 0,
        pagination: paginationDebounce = 0,
    } = debounce;
    const {
        filterCacheKey = getTableResourceFromReportResource(resource),
        enableSchema = false,
        initialState = {},
        extraParams = {},
    } = useContext<ITableCoreResourceContext<N>>(TableCoreResourceContext);
    const { filters } = useFiltersByKey(filterCacheKey);
    const {
        data: { columns: columnSchema },
    } = useTableSchema(resource);
    const {
        columnVisibilityState: columnVisibilityStateConsumer,
        ...restInitialTableStateConsumer
    } = initialTableState;
    const { columnVisibilityState, ...restInitialState } = initialState;
    const tableState = useReactTableState({
        ...restInitialTableStateConsumer,
        ...restInitialState,
        columnVisibilityState: { ...columnVisibilityStateConsumer, ...columnVisibilityState },
    });
    const { columnOrder, pagination, sorting, columnVisibility, givebutterColumnFilters } =
        tableState;
    const debouncedPagination = useDebounce(pagination, paginationDebounce);
    const debouncedSorting = useDebounce(sorting, sortingDebounce);
    const debouncedColumnVisibility = useDebounce(columnVisibility, columnVisibilityDebounce);
    const [search, setSearch] = usePersistentSearch();
    const debouncedSearch = useDebounce(search, searchDebounce);
    const searchState = {
        search,
        setSearch,
    };

    const stableSearch = useMemo(() => debouncedSearch, [debouncedSearch]);
    const stablePagination = useMemo(() => debouncedPagination, [debouncedPagination]);
    const stableFilters = useMemo(() => filters, [filters]);
    const stableExtraParams = useMemo(() => extraParams, [extraParams]);
    const stableParamsConsumer = useMemo(() => paramsConsumer, [paramsConsumer]);
    const stableColumnOrder = useMemo(() => columnOrder, [columnOrder]);
    const stableSorting = useMemo(
        () =>
            debouncedSorting.length > 0
                ? { [`sortBy${debouncedSorting[0].desc ? 'Desc' : ''}`]: debouncedSorting[0].id }
                : {},
        [debouncedSorting],
    );

    const stableColumns = useMemo(
        () =>
            enableSchema
                ? Object.entries(debouncedColumnVisibility)
                      .filter(([, isVisible]) => isVisible)
                      .map(([key]): Column | undefined => {
                          const possibleCol = columnSchema.find(col => col.name === key);
                          const columnFilters = givebutterColumnFilters[key];
                          const transformedFilters = columnFilters
                              ?.map(group => group.filters.map(transformFilterIntoNextRequest))
                              .flat();

                          if (possibleCol) {
                              const nextColumn: Column = {
                                  name: possibleCol.name,
                                  type: possibleCol.type,
                                  ...(transformedFilters && { filters: transformedFilters }),
                              };
                              if (possibleCol.type === 'simple') {
                                  return nextColumn;
                              } else {
                                  return {
                                      ...nextColumn,
                                      function: 'SUM',
                                  };
                              }
                          }
                      })
                      .filter(Boolean)
                      .sort((a, b) => {
                          if (a && b) {
                              const aIndex = stableColumnOrder.indexOf(a.name);
                              const bIndex = stableColumnOrder.indexOf(b.name);
                              return aIndex - bIndex;
                          }

                          return 0;
                      })
                : undefined,
        [
            enableSchema,
            debouncedColumnVisibility,
            givebutterColumnFilters,
            columnSchema,
            stableColumnOrder,
        ],
    );

    const params = {
        page: stablePagination.pageIndex + 1,
        per_page: stablePagination.pageSize,
        query: stableSearch,
        filters: stableFilters,
        columns: stableColumns,
        ...stableSorting,
        ...stableExtraParams,
        ...stableParamsConsumer,
    };

    return {
        tableState,
        searchState,
        params,
    };
};

export { useCoreTableState };
